import React from 'react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import {
  mapDemoFormToPayload,
  submitLandingLead,
} from './api/landingLeads';

const INITIAL_VALUES = {
  name: '',
  workEmail: '',
  phone: '',
  jobTitle: '',
  companyName: '',
  country: 'United States',
  monthlyUsers: '',
  hearAboutUs: '',
  comments: '',
};

const US_PHONE_DIGIT_LIMIT = 10;

const getPhoneDigits = (value) =>
  value.replace(/\D/g, '').slice(0, US_PHONE_DIGIT_LIMIT);

const formatUsPhoneNumber = (value) => {
  const digits = getPhoneDigits(value);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const isValidUsPhoneNumber = (value) =>
  getPhoneDigits(value).length === US_PHONE_DIGIT_LIMIT;

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  workEmail: Yup.string()
    .trim()
    .email('Enter a valid work email')
    .required('Work email is required'),
  phone: Yup.string()
    .trim()
    .required('Phone number is required')
    .test(
      'valid-us-phone',
      'Enter a valid 10-digit US phone number',
      (value) => isValidUsPhoneNumber(value)
    ),
  jobTitle: Yup.string().trim().required('Job title is required'),
  companyName: Yup.string().trim().required('Company name is required'),
  country: Yup.string().required('Country is required'),
  monthlyUsers: Yup.string(),
  hearAboutUs: Yup.string().trim(),
  comments: Yup.string().trim(),
});

const COUNTRIES = ['United States'];

const MONTHLY_USERS = [
  'Under 10K',
  '10K – 50K',
  '50K – 100K',
  '100K – 500K',
  '500K+',
];

const RequiredMark = () => <span className="demo-modal__required">*</span>;

const FieldError = ({ touched, error }) => {
  if (!touched || !error) return null;
  return <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>;
};

const getSubmitErrorMessage = (error) => {
  const apiMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.detail;

  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }

  return 'Unable to submit your request. Please try again.';
};

const DemoModal = ({ show, onHide, onSuccess }) => {
  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm, setSubmitting, setStatus }) => {
        setStatus(null);

        try {
          const { data } = await submitLandingLead(mapDemoFormToPayload(values));
          const successMessage =
            data?.message || 'Thank you. Your request has been submitted.';

          resetForm();
          onHide();
          onSuccess?.(successMessage);
        } catch (error) {
          setStatus(getSubmitErrorMessage(error));
        } finally {
          setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        values,
        resetForm,
        isSubmitting,
        handleSubmit,
        status,
        setStatus,
        setFieldValue,
      }) => {
        const handlePhoneChange = (event) => {
          setFieldValue('phone', formatUsPhoneNumber(event.target.value));
        };

        const handleClose = () => {
          resetForm();
          setStatus(null);
          onHide();
        };

        return (
          <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            scrollable
            className="demo-modal"
            aria-labelledby="demo-modal-title"
          >
            <Modal.Header closeButton className="demo-modal__header border-0 pb-0">
              <Modal.Title
                id="demo-modal-title"
                as="h2"
                className="demo-modal__title"
              >
                Schedule a LIVE demo today
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="demo-modal__body">
              <FormikForm noValidate onSubmit={handleSubmit}>
                {status ? (
                  <Alert
                    variant="danger"
                    className="demo-modal__alert"
                    onClose={() => setStatus(null)}
                    dismissible
                  >
                    {status}
                  </Alert>
                ) : null}

                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group controlId="demo-name">
                      <Form.Label>
                        Name
                        <RequiredMark />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder="Enter your name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <FieldError touched={touched.name} error={errors.name} />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-work-email">
                      <Form.Label>
                        Work Email
                        <RequiredMark />
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="workEmail"
                        placeholder="Enter your work email"
                        value={values.workEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.workEmail && !!errors.workEmail}
                      />
                      <FieldError
                        touched={touched.workEmail}
                        error={errors.workEmail}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-phone">
                      <Form.Label>
                        Phone Number
                        <RequiredMark />
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        placeholder="e.g. 425 601 7295"
                        value={values.phone}
                        onChange={handlePhoneChange}
                        onBlur={handleBlur}
                        inputMode="numeric"
                        autoComplete="tel"
                        maxLength={12}
                        isInvalid={touched.phone && !!errors.phone}
                      />
                      <FieldError touched={touched.phone} error={errors.phone} />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-job-title">
                      <Form.Label>
                        Job title
                        <RequiredMark />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="jobTitle"
                        placeholder="Enter your designation"
                        value={values.jobTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.jobTitle && !!errors.jobTitle}
                      />
                      <FieldError
                        touched={touched.jobTitle}
                        error={errors.jobTitle}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-company-name">
                      <Form.Label>
                        Company Name
                        <RequiredMark />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="companyName"
                        value={values.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.companyName && !!errors.companyName}
                      />
                      <FieldError
                        touched={touched.companyName}
                        error={errors.companyName}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-country">
                      <Form.Label>
                        Country
                        <RequiredMark />
                      </Form.Label>
                      <Form.Select
                        name="country"
                        value={values.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.country && !!errors.country}
                      >
                        <option value="">Please Select</option>
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </Form.Select>
                      <FieldError
                        touched={touched.country}
                        error={errors.country}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-monthly-users">
                      <Form.Label>
                        Total Monthly Active Users/Unique Visitors
                      </Form.Label>
                      <Form.Select
                        name="monthlyUsers"
                        value={values.monthlyUsers}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.monthlyUsers && !!errors.monthlyUsers
                        }
                      >
                        <option value="">Please Select</option>
                        {MONTHLY_USERS.map((range) => (
                          <option key={range} value={range}>
                            {range}
                          </option>
                        ))}
                      </Form.Select>
                      <FieldError
                        touched={touched.monthlyUsers}
                        error={errors.monthlyUsers}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-hear-about-us">
                      <Form.Label>How did you hear about us?</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="hearAboutUs"
                        value={values.hearAboutUs}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group controlId="demo-comments">
                      <Form.Label>Comments</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="comments"
                        placeholder="Enter use cases or specific requirements"
                        value={values.comments}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <p className="demo-modal__consent mb-3 mt-4">
                  By submitting this form, you agree to Lien Logic&apos;s Privacy
                  Policy, Terms of Service &amp; to receive marketing
                  communications.
                </p>

                <Button
                  type="submit"
                  className="demo-modal__submit w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit'}
                </Button>
              </FormikForm>
            </Modal.Body>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default DemoModal;
